import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Marquee from 'react-fast-marquee';
import { Area, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, BarChart, Bar, CartesianGrid, Cell } from 'recharts';
import { Zap, ArrowRight, TrendingUp, ChevronDown, ChevronLeft, ChevronRight, Users, Star, Check, Pencil, Clock, ArrowDown, ArrowUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display helpers — pseudonyms now come from the backend
function getDisplayName(bid, currentUser) {
  const name = bid.wearer_name || 'Mystery Student';
  if (currentUser && bid.user_id === currentUser.user_id) {
    return name + ' (you)';
  }
  return name;
}
function getInitial(bid, currentUser) {
  if (currentUser && bid.user_id === currentUser.user_id) return '★';
  return (bid.wearer_name || 'M')[0];
}
function getAvatarColor(bid) {
  return bid.avatar_color || '#E8C86A';
}

function formatActivity(event) {
  const m = event.metadata || {};
  // Pseudonyms are now provided by the backend
  const wearerName = m.wearer_name || 'Someone';
  const userName = m.user_name || 'Someone';
  switch (event.event_type) {
    case 'bid_raised': return `${m.startup_name} raised ask from $${m.old_bid} to $${m.new_bid}`;
    case 'bid_lowered': return `${m.startup_name} lowered ask to $${m.new_bid}`;
    case 'wearer_bid_placed': return `${wearerName} wants $${m.asking_price} for a tee`;
    case 'bid_updated': return `${wearerName} updated ask from $${m.old_price} to $${m.new_price}`;
    case 'bid_accepted': return `${m.startup_name} accepted ${wearerName}'s ask at $${m.asking_price}`;
    case 'claim_created': return `${userName} claimed ${m.quantity} ${m.title}`;
    case 'sold_out': return `${m.startup_name} sold out ${m.total} shirts!`;
    case 'listing_live': return `${m.startup_name} listed ${m.title} at $${m.bid}/shirt`;
    default: return 'New activity on the market';
  }
}

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function PriceTooltip({ active, payload }) {
  if (active && payload?.length) {
    return (
      <div className="bg-[#121212] text-[#F6F1E8] border-[2px] border-[#121212] rounded-lg px-3 py-2 text-xs font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF6B6B' }} />
          ${payload[0]?.value || 0}
        </div>
      </div>
    );
  }
  return null;
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState({});
  const [bidStats, setBidStats] = useState(null);
  const [myBid, setMyBid] = useState(null);
  const [marketOpen, setMarketOpen] = useState(true);
  const [marketReopensAt, setMarketReopensAt] = useState(null);
  const [countdown, setCountdown] = useState(null); // { mins, secs } or null
  const countdownRef = useRef(null);

  // Bid placement
  const [askingPrice, setAskingPrice] = useState('');
  const [placing, setPlacing] = useState(false);
  const [bidSuccess, setBidSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Market data (from BrowseListings)
  const [allBids, setAllBids] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [bidPage, setBidPage] = useState(0);
  const BIDS_PER_PAGE = 5;
  const [accepting, setAccepting] = useState(null);

  const isStartup = user?.role === 'startup';
  const isAdmin = user?.role === 'admin';
  const isPending = user?.role === 'wearer' && user?.approval_status === 'pending';
  const isRejected = user?.role === 'wearer' && user?.approval_status === 'rejected';
  const isApproved = user?.role === 'wearer' && user?.approval_status === 'approved';
  const isNotOnboarded = user && !user.onboarded && !isStartup && !isAdmin;
  const isLockedOut = user && !isStartup && !isAdmin && !isApproved; // pending, rejected, or not onboarded

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      axios.get(`${API}/activity?limit=15`).catch(() => ({ data: [] })),
      axios.get(`${API}/stats`).catch(() => ({ data: {} })),
      axios.get(`${API}/wearer-bids/stats`).catch(() => ({ data: null })),
      axios.get(`${API}/settings`).catch(() => ({ data: {} })),
      axios.get(`${API}/wearer-bids?sort=${sortBy}`).catch(() => ({ data: { bids: [] } })),
    ]).then(([actRes, statsRes, bidStatsRes, settingsRes, bidsRes]) => {
      setActivity(actRes.data);
      setStats(statsRes.data);
      setBidStats(bidStatsRes.data);
      setMarketOpen(settingsRes.data?.market_open !== false);
      setMarketReopensAt(settingsRes.data?.market_reopens_at || null);
      setAllBids(bidsRes.data?.bids || []);
    }).finally(() => setIsLoading(false));
  }, [sortBy]);

  // Poll settings every 15s so countdown/market state updates automatically
  useEffect(() => {
    const poll = setInterval(() => {
      axios.get(`${API}/settings`).then(r => {
        const newOpen = r.data?.market_open !== false;
        const newReopens = r.data?.market_reopens_at || null;
        setMarketOpen(newOpen);
        if (newReopens !== marketReopensAt) setMarketReopensAt(newReopens);
      }).catch(() => { });
    }, 15000);
    return () => clearInterval(poll);
  }, [marketReopensAt]);

  // Countdown timer effect
  useEffect(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    if (!marketReopensAt) {
      setCountdown(null);
      return;
    }

    const reopensTime = new Date(marketReopensAt).getTime();

    const tick = () => {
      const now = Date.now();
      const diff = reopensTime - now;
      if (diff <= 0) {
        // Countdown expired — market is fully open now
        setCountdown(null);
        setMarketReopensAt(null);
        setMarketOpen(true);
        if (countdownRef.current) clearInterval(countdownRef.current);
        // Re-fetch to get latest state
        axios.get(`${API}/settings`).then(r => {
          setMarketOpen(r.data.market_open !== false);
          setMarketReopensAt(r.data.market_reopens_at || null);
        }).catch(() => { });
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown({ mins, secs });
    };

    tick(); // run immediately
    countdownRef.current = setInterval(tick, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [marketReopensAt]);

  useEffect(() => {
    if (user && !isStartup) {
      axios.get(`${API}/wearer-bids/mine`, { withCredentials: true })
        .then(r => {
          if (r.data) {
            setMyBid(r.data);
            setAskingPrice(String(r.data.asking_price));
          }
        })
        .catch(() => { });
    }
  }, [user, isStartup]);

  const placeBid = useCallback(async () => {
    const price = parseFloat(askingPrice);
    if (!price || price <= 0) return;
    if (!user) { navigate('/login'); return; }
    setPlacing(true);
    setBidSuccess('');
    try {
      const r = await axios.post(`${API}/wearer-bids`, {
        garment_type: 'tee',
        asking_price: price
      }, { withCredentials: true });
      setMyBid(r.data);
      setIsEditing(false);
      setBidSuccess(myBid ? 'Ask updated!' : 'Your ask is live!');
      axios.get(`${API}/wearer-bids/stats`).then(r => setBidStats(r.data)).catch(() => { });
      axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => { });
      axios.get(`${API}/wearer-bids?sort=${sortBy}`).then(r => setAllBids(r.data?.bids || [])).catch(() => { });
      setTimeout(() => setBidSuccess(''), 4000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to place ask');
    } finally {
      setPlacing(false);
    }
  }, [askingPrice, user, navigate, myBid, sortBy]);

  const handleAcceptBid = async (bidId) => {
    setAccepting(bidId);
    try {
      await axios.post(`${API}/wearer-bids/${bidId}/accept`, {}, { withCredentials: true });
      // Refresh bids list
      const [bidsRes, statsRes] = await Promise.all([
        axios.get(`${API}/wearer-bids?sort=${sortBy}`).catch(() => ({ data: { bids: [] } })),
        axios.get(`${API}/wearer-bids/stats`).catch(() => ({ data: null })),
      ]);
      setAllBids(bidsRes.data?.bids || []);
      setBidStats(statsRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to accept ask');
    } finally {
      setAccepting(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const price = parseFloat(askingPrice);
      if (price > 0 && price % 10 === 0) placeBid();
    }
  };

  const chartData = bidStats?.price_history?.map((p, i) => ({
    idx: i,
    price: p.asking_price,
  })) || [];

  const hasActiveBid = myBid && myBid.status === 'active';
  const bidAccepted = myBid && myBid.status === 'accepted';

  // Histogram computation — bucket bids into $100 ranges
  const BUCKET_SIZE = 10;
  const histogram = [];
  if (allBids.length > 0) {
    const bucketMap = {};
    allBids.forEach(b => {
      const bucketStart = Math.floor(b.asking_price / BUCKET_SIZE) * BUCKET_SIZE;
      if (!bucketMap[bucketStart]) bucketMap[bucketStart] = { min: bucketStart, max: bucketStart + BUCKET_SIZE - 1, label: `$${bucketStart}`, count: 0 };
      bucketMap[bucketStart].count++;
    });
    Object.values(bucketMap)
      .sort((a, b) => a.min - b.min)
      .forEach(bucket => histogram.push(bucket));
  }
  const maxBucketCount = Math.max(...histogram.map(b => b.count), 1);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      {/* Marquee Ticker */}
      {activity.length > 0 ? (
        <div className="border-b-[3px] border-[#121212]" style={{ backgroundColor: '#E8C86A' }}>
          <Marquee speed={40} gradient={false} pauseOnHover>
            {activity.map((evt, i) => (
              <span key={i} className="font-mono text-sm font-bold px-8 py-2.5 inline-block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <Zap className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" strokeWidth={3} />
                {formatActivity(evt)}
              </span>
            ))}
          </Marquee>
        </div>
      ) : isLoading && (
        <div className="border-b-[3px] border-[#121212] ticker-skeleton" style={{ backgroundColor: '#E8C86A' }}>
          <div className="flex items-center gap-10 px-8 py-2.5">
            {[140, 200, 160, 180, 120, 170, 150].map((w, i) => (
              <div key={i} className="flex items-center gap-2 flex-shrink-0">
                <div className="w-3.5 h-3.5 rounded bg-[#121212]/10" />
                <div className="h-3.5 rounded bg-[#121212]/10" style={{ width: w }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-6 md:pt-20 md:pb-10">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hero-headline">
            A T-Shirt is AD space.
            <br />
            <span style={{ color: '#FF6B6B' }}>Buy it.</span>
          </h1>
          <p className="text-base md:text-lg font-medium max-w-xl mx-auto" style={{ color: '#555' }}>
            Pay attendees to wear your startups T-Shirt at <span className="font-bold " style={{ color: '#ff6602' }}>Y-Combinator</span> Startup School India.
          </p>
        </div>

        {/* Stats strip */}
        {!isLockedOut && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto mb-10">
            {[
              { label: 'Students Signed Up', value: stats.total_wearers || 0, icon: Users, bg: '#FF6B6B', iconColor: '#121212' },
              // TODO: Re-enable when startups are integrated — just uncomment the line below:
              // { label: 'T-Shirts Sold', value: stats.total_acceptances || 0, icon: Check, bg: '#E8C86A', iconColor: '#121212' },
              { label: 'Startups', value: stats.total_startups || 0, icon: Star, bg: '#6EE7B7', iconColor: '#121212' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3 border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl px-4 py-3" style={{ backgroundColor: s.bg }}>
                <s.icon className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} style={{ color: s.iconColor }} />
                <div className="flex items-baseline sm:items-start sm:flex-col gap-2 sm:gap-0">
                  <div className="font-mono text-lg font-bold leading-tight" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#121212' }}>{s.value}</div>
                  <div className="text-[10px] font-black uppercase tracking-wider whitespace-nowrap" style={{ color: '#121212' }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Startup users see CTA to their dashboard instead of bid form */}
        {isStartup && (
          <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 md:p-8 max-w-2xl mx-auto mb-6 text-center" data-testid="startup-cta-card">
            <div className="text-sm font-black uppercase tracking-wider mb-3" style={{ color: '#888' }}>You're a startup</div>
            <p className="text-base font-bold mb-4">Browse student asks and accept the ones you want.</p>
            <Link
              to="/dashboard/startup"
              className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-lg border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 inline-flex items-center gap-2"
              data-testid="go-startup-dashboard"
            >
              Go to Dashboard <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </Link>
          </div>
        )}

        {/* Not onboarded banner */}
        {isNotOnboarded && !isPending && !isRejected && (
          <div className="bg-[#D8DFEA]/50 border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5 max-w-2xl mx-auto mb-6 text-center" data-testid="not-onboarded-banner">
            <div className="font-black uppercase text-base mb-1">Complete Your Setup</div>
            <p className="text-sm font-medium mb-3" style={{ color: '#666' }}>
              Finish onboarding to start placing asks on the market.
            </p>
            <Link to="/onboarding" className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#121212] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-5 py-2.5 inline-flex items-center gap-2" data-testid="complete-onboarding-link">
              Complete Onboarding <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        )}

        {/* Pending approval banner */}
        {isPending && (
          <div className="bg-[#E8C86A]/30 border-[3px] border-[#E8C86A] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5 max-w-2xl mx-auto mb-6 text-center" data-testid="pending-approval-banner">
            <Clock className="w-8 h-8 mx-auto mb-2" strokeWidth={2} style={{ color: '#E8C86A' }} />
            <div className="font-black uppercase text-base mb-1">Under Review</div>
            <p className="text-sm font-medium mb-4" style={{ color: '#666' }}>
              Your YC acceptance screenshot is being reviewed. You'll be able to place asks once approved.
            </p>
            <div className="bg-[#121212] rounded-xl p-5 text-center">
              <p className="text-base font-bold mb-2" style={{ color: '#F6F1E8' }}>
                Want us to accept you on priority? 🚀
              </p>
              <p className="text-sm font-medium mb-3" style={{ color: '#aaa' }}>
                Tweet at{' '}
                <a href="https://twitter.com/TanmaiVully" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline" style={{ color: '#FF6B6B' }}>@TanmaiVully</a>{' '}
                &{' '}
                <a href="https://twitter.com/prabhav_guddati" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline" style={{ color: '#FF6B6B' }}>@prabhav_guddati</a>
                {' '} with your thoughts on this and tag your favourite startups too. We'll get back to you with some cool stickers along the way 🙌.
              </p>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Hey @ycombinator, @TanmaiVully & @prabhav_guddati made https://www.surfacearea.shop/ to turned everyone at YC Startup School India into a human billboard!')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#E8C86A] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#F6F1E8] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#F6F1E8] active:translate-y-[2px] active:shadow-none transition-all rounded-xl px-10 py-2.5"
                data-testid="tweet-to-expedite-btn"
              >
                𝕏
              </a>
            </div>
          </div>
        )}

        {/* Rejected banner */}
        {isRejected && (
          <div className="bg-[#FF6B6B]/15 border-[3px] border-[#FF6B6B] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5 max-w-2xl mx-auto mb-6 text-center" data-testid="rejected-banner">
            <div className="font-black uppercase text-base mb-1" style={{ color: '#FF6B6B' }}>Not Approved</div>
            <p className="text-sm font-medium mb-3" style={{ color: '#666' }}>
              Your YC screenshot couldn't be verified. Please send us your LinkedIn, Twitter/X handle, and a proper screenshot of your YC acceptance so we can verify you.
            </p>
            <div
              className="inline-flex items-center gap-2 bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl px-5 py-2.5"
              data-testid="rejected-contact-support-btn"
            >
              Email us at support@surfacearea.shop
            </div>
          </div>
        )}

        {/* Bid Accepted Banner */}
        {!isStartup && bidAccepted && (
          <div className="bg-[#6EE7B7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5 max-w-2xl mx-auto mb-6 text-center animate-fade-in-up" data-testid="bid-accepted-banner">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Check className="w-5 h-5" strokeWidth={3} />
              <span className="font-black uppercase text-base">Your ask was accepted!</span>
            </div>
            <p className="text-sm font-medium mb-3">
              {myBid.acceptance?.startup_name || 'A startup'} picked you at ${myBid.asking_price}.
            </p>
            <Link to="/my-tshirt" className="bg-[#121212] text-[#F6F1E8] font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-5 py-2.5 inline-block" data-testid="go-to-dashboard-btn">
              Complete Fulfillment
            </Link>
          </div>
        )}

        {/* Active Bid Display */}
        {!isStartup && isApproved && hasActiveBid && !isEditing && (
          <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 md:p-8 max-w-2xl mx-auto mb-6" data-testid="active-bid-card">
            <div className="text-xs font-black uppercase tracking-wider mb-3" style={{ color: '#E8663C' }}>Remember you only get what you ask for!</div>
            <div className="text-xs font-black uppercase tracking-wider mb-3" style={{ color: '#888' }}>Your active ask</div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-4xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="my-bid-price">${myBid.asking_price}</div>
                <div className="text-sm font-medium mt-1" style={{ color: '#666' }}>per T-Shirt</div>
              </div>
              <span className="bg-[#6EE7B7] border-[2px] border-[#121212] shadow-[2px_2px_0px_0px_#121212] text-xs font-black px-3 py-1 rounded-full uppercase">Live</span>
            </div>
            {bidSuccess && (
              <div className="bg-[#6EE7B7] border-[2px] border-[#121212] rounded-xl p-3 text-sm font-bold text-center mb-3" data-testid="bid-success-msg">{bidSuccess}</div>
            )}
            <button onClick={() => setIsEditing(true)} className="w-full bg-[#E8C86A] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 flex items-center justify-center gap-2" data-testid="edit-bid-btn">
              <Pencil className="w-4 h-4" strokeWidth={3} /> Update My Ask
            </button>
          </div>
        )}

        {/* Bid Placement / Edit Form — only for approved non-startup users (or not logged in) */}
        {!isStartup && !isLockedOut && (!hasActiveBid || isEditing) && !bidAccepted && (
          <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 md:p-8 max-w-2xl mx-auto" data-testid="bid-form-card">
            <div className="text-sm font-black uppercase tracking-wider mb-4" style={{ color: '#888' }}>
              {isEditing ? 'Update your ask' : 'Place your ask'}
            </div>
            <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-4">
              <div className="flex items-center gap-2 text-base font-bold whitespace-nowrap" style={{ color: '#444' }}>
                I'll wear a T-Shirt for
              </div>
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-mono text-xl font-bold z-10 pointer-events-none" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$</span>
                <input
                  type="number"
                  value={askingPrice}
                  onChange={e => setAskingPrice(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="300"
                  min={10}
                  step={10}
                  className="w-full bg-white border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl py-3 pl-10 pr-4 font-mono text-xl font-bold focus:outline-none focus:-translate-y-[1px] focus:shadow-[4px_4px_0px_0px_#121212] transition-all"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  data-testid="asking-price-input"
                />
              </div>
              {askingPrice && parseFloat(askingPrice) > 0 && parseFloat(askingPrice) % 10 !== 0 && (
                <p className="text-xs font-bold mt-1.5" style={{ color: '#FF4444' }}>* Ask must be a multiple of $10</p>
              )}
            </div>

            {bidSuccess && !hasActiveBid && (
              <div className="bg-[#6EE7B7] border-[2px] border-[#121212] rounded-xl p-3 text-sm font-bold text-center mb-3" data-testid="bid-success-msg">{bidSuccess}</div>
            )}

            <div className="flex gap-3">
              <button
                onClick={placeBid}
                disabled={!askingPrice || parseFloat(askingPrice) <= 0 || parseFloat(askingPrice) % 10 !== 0 || placing}
                className="flex-1 bg-[#FF6B6B] text-[#121212] font-black uppercase text-lg border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="place-bid-btn"
              >
                {placing ? 'Placing...' : isEditing ? 'Update Ask' : user ? 'Place My Ask' : 'Sign In & Ask'}
              </button>
              {isEditing && (
                <button onClick={() => { setIsEditing(false); setAskingPrice(String(myBid.asking_price)); }} className="bg-[#F6F1E8] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl px-5 py-4" data-testid="cancel-edit-btn">
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}


      </div>

      {/* ═══ Market Header ═══ */}
      {!isLockedOut && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="market-section-title">
              The Market
            </h2>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="font-mono text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${bidStats?.avg_price || 0}</div>
                <div className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>Median Ask</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${bidStats?.min_price || 0}</div>
                <div className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>Floor</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${bidStats?.max_price || 0}</div>
                <div className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>Ceiling</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart / Market Napping */}
      {marketOpen && !countdown ? (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          {isLoading ? (
            <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 bid-skeleton-card">
              <div className="flex items-center justify-between mb-4">
                <div className="space-y-2">
                  <div className="bid-skeleton-bar h-5 w-36" />
                  <div className="bid-skeleton-bar h-3 w-28" />
                </div>
                <div className="space-y-2 flex flex-col items-end">
                  <div className="bid-skeleton-bar h-7 w-24" />
                  <div className="bid-skeleton-bar h-3 w-14" />
                </div>
              </div>
              <div className="bid-skeleton-bar h-52 w-full" style={{ borderRadius: 12 }} />
              <div className="flex items-center justify-between mt-3">
                <div className="bid-skeleton-bar h-3 w-48" />
                <div className="bid-skeleton-bar h-3 w-14" />
              </div>
            </div>
          ) : chartData.length > 2 ? (
            <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 animate-fade-in-up" data-testid="price-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <TrendingUp className="w-5 h-5 inline mr-2 -mt-1" strokeWidth={2.5} />
                    Market Price
                  </h2>
                  <p className="text-xs font-medium mt-1" style={{ color: '#888' }}>Asking prices over time</p>
                </div>
                <div className="text-right">
                  <div className="font-mono text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${bidStats?.avg_price || 0}</div>
                  <div className="text-xs font-bold uppercase" style={{ color: '#888' }}>Median Ask</div>
                </div>
              </div>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <defs>
                      <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF6B6B" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FF6B6B" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="idx" hide />
                    <YAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', fill: '#888' }} axisLine={{ stroke: '#ccc' }} tickLine={false} tickFormatter={v => `$${v}`} domain={['dataMin - 50', 'dataMax + 50']} width={55} />
                    <Tooltip content={<PriceTooltip />} />
                    <Area type="linear" dataKey="price" stroke="#FF6B6B" strokeWidth={3} fill="url(#priceGradient)" dot={false} activeDot={{ r: 5, strokeWidth: 3, stroke: '#121212', fill: '#FF6B6B' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-4 text-xs font-mono" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#888' }}>
                  <span>Min: ${bidStats?.min_price || 0}</span>
                  <span>{bidStats?.active_bids || 0} active asks</span>
                  <span>Max: ${bidStats?.max_price || 0}</span>
                </div>
                <div className="flex items-center gap-3 text-xs font-bold">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block rounded" style={{ backgroundColor: '#FF6B6B', opacity: 0.6 }} /> Price</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : (!marketOpen || countdown) && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <div className="bg-[#121212] border-[3px] border-[#E8C86A] shadow-[6px_6px_0px_0px_#E8C86A] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-testid="market-napping-card">
            {countdown ? (
              <>
                <div className="flex items-center justify-center gap-3 mb-4" data-testid="countdown-timer">
                  {String(countdown.mins).padStart(2, '0').split('').map((d, i) => (
                    <span key={`m${i}`} className="inline-flex items-center justify-center w-16 h-20 md:w-20 md:h-24 rounded-xl border-[2px] border-white/20" style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                      fontWeight: 800,
                      color: '#FFFFFF',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.03) 100%)',
                      textShadow: '0 0 20px rgba(255,255,255,0.6), 0 0 40px rgba(255,255,255,0.25)',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '0.05em',
                    }}>{d}</span>
                  ))}
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#FFFFFF', textShadow: '0 0 15px rgba(255,255,255,0.5)', animation: 'pulse 1s infinite' }}>:</span>
                  {String(countdown.secs).padStart(2, '0').split('').map((d, i) => (
                    <span key={`s${i}`} className="inline-flex items-center justify-center w-16 h-20 md:w-20 md:h-24 rounded-xl border-[2px] border-white/20" style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                      fontWeight: 800,
                      color: '#FFFFFF',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.03) 100%)',
                      textShadow: '0 0 20px rgba(255,255,255,0.6), 0 0 40px rgba(255,255,255,0.25)',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '0.05em',
                    }}>{d}</span>
                  ))}
                </div>
                <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#F6F1E8' }}>Market Opening Soon</h2>
                <p className="text-sm md:text-base font-medium mb-1" style={{ color: '#999' }}>The market is warming up. Hang tight.</p>
                <p className="text-sm md:text-base font-medium mb-6" style={{ color: '#999' }}>You can still place your asks — they'll go live when the clock hits zero.</p>
                <div className="w-full max-w-xs mx-auto mb-4">
                  <div className="h-2 rounded-full" style={{ backgroundColor: '#333' }}>
                    <div
                      className="h-2 rounded-full transition-all duration-1000"
                      style={{
                        backgroundColor: '#E8C86A',
                        width: `${Math.max(0, 100 - ((countdown.mins * 60 + countdown.secs) / 30) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="inline-block bg-[#E8C86A] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-6 py-3 cursor-default" data-testid="opening-soon-badge">
                  Opening Soon
                </div>
              </>
            ) : (
              <>
                <div className="nap-glow" />
                <div className="flex items-end justify-center gap-1 mb-5 h-16">
                  <span className="nap-z">z</span>
                  <span className="nap-z">z</span>
                  <span className="nap-z">Z</span>
                </div>
                <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#F6F1E8' }}>BrB doing things that don't scale.</h2>
                <p className="text-sm md:text-base font-medium mb-1" style={{ color: '#999' }}>The Market opens on 04/15 at 10:00 A.M PST, so that everyone gets the most competitive price.</p>
                <p className="text-sm md:text-base font-medium mb-6" style={{ color: '#999' }}>Grab a coffee. We'll be back before it gets cold.</p>
                <div className="inline-block bg-[#EDE3C7] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-6 py-3 cursor-default" data-testid="check-back-btn">
                  Check Back Soon
                </div>
              </>
            )}
          </div>
        </div>
      )}



      {/* ═══ Market Section (Order Book + All Bids) ═══ */}
      {!isLockedOut && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Price Distribution (left) */}
            <div className="lg:col-span-2">
              <div className="bg-[#EDE3C7] border-[2px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-2xl overflow-hidden" data-testid="order-book">
                <div className="bg-[#121212] text-[#F6F1E8] px-5 py-3 flex items-center justify-between">
                  <h3 className="font-black uppercase text-sm tracking-wider">Order Book</h3>
                  <span className="text-xs font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#888' }}>
                    {allBids.length} ask{allBids.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {isLoading ? (
                  <div className="py-12 text-center">
                    <div className="animate-spin w-6 h-6 border-3 border-[#121212] border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : histogram.length === 0 ? (
                  <div className="py-12 text-center text-sm font-medium" style={{ color: '#888' }}>No active asks</div>
                ) : (
                  <div className="px-3 pt-4 pb-2">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart barCategoryGap={0} layout="vertical" data={histogram} margin={{ top: 5, right: 15, bottom: 5, left: 5 }}>
                        {/* <CartesianGrid strokeDasharray="3 3" stroke="#121212" strokeOpacity={0.08} horizontal={false} /> */}
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fill: '#888' }}
                          axisLine={{ stroke: 'rgba(18,18,18,0.35)', strokeWidth: 1 }}
                          tickLine={false}
                          domain={[0, 'dataMax + 1']}
                        />
                        <YAxis
                          type="category"
                          dataKey="label"
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fill: '#888' }}
                          axisLine={{ stroke: 'rgba(18,18,18,0.35)', strokeWidth: 1 }}
                          tickLine={false}
                          width={50}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(18,18,18,0.05)' }}
                          content={({ active, payload }) => {
                            if (active && payload?.length) {
                              const d = payload[0].payload;
                              return (
                                <div className="bg-[#121212] text-[#F6F1E8] border-[2px] border-[#121212] rounded-lg px-3 py-2 text-xs font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                  <div className="font-bold">${d.min}–${d.max}</div>
                                  <div style={{ color: '#6EE7B7' }}>{d.count} student{d.count !== 1 ? 's' : ''}</div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="count" radius={[0, 2, 2, 0]} maxBarSize={28}>
                          {histogram.map((entry, index) => (
                            <Cell key={index} fill="rgba(110,231,183,0.55)" stroke="#121212" strokeWidth={0.5} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* Individual bids (right) */}
            <div className="lg:col-span-3">
              {/* Sort */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black uppercase tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Users className="w-5 h-5 inline mr-2 -mt-0.5" strokeWidth={2.5} /> All Asks
                </h3>
                <div className="flex gap-2">
                  {[
                    { value: 'newest', label: 'New' },
                    { value: 'cheapest', label: 'Low', icon: ArrowDown },
                    { value: 'expensive', label: 'High', icon: ArrowUp },
                  ].map(o => (
                    <button
                      key={o.value}
                      onClick={() => { setSortBy(o.value); setBidPage(0); }}
                      className={`text-xs font-black uppercase tracking-wider border-[2px] border-[#121212] rounded-full px-3 py-1 transition-all ${sortBy === o.value ? 'bg-[#121212] text-[#F6F1E8]' : 'bg-[#F6F1E8] shadow-[2px_2px_0px_0px_#121212] hover:-translate-y-[1px]'
                        }`}
                      data-testid={`sort-pill-${o.value}`}
                    >
                      {o.icon && <o.icon className="w-3 h-3 inline mr-0.5 -mt-0.5" strokeWidth={3} />}
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {isLoading && (
                <div className="space-y-3">
                  {[180, 140, 200, 160, 120].map((w, i) => (
                    <div
                      key={i}
                      className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-2xl p-4 flex items-center gap-4 bid-skeleton-card"
                    >
                      <div className="w-10 h-10 rounded-xl border-[2px] border-[#121212] flex-shrink-0 bid-skeleton-bar" />
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="bid-skeleton-bar h-4" style={{ width: w }} />
                        <div className="bid-skeleton-bar h-3" style={{ width: w * 0.6 }} />
                      </div>
                      <div className="flex-shrink-0 space-y-1.5 flex flex-col items-end">
                        <div className="bid-skeleton-bar h-6 w-16" />
                        <div className="bid-skeleton-bar h-3 w-10" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isLoading && allBids.length === 0 && (
                <div className="text-center py-16 bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl">
                  <Users className="w-12 h-12 mx-auto mb-4" strokeWidth={1.5} style={{ color: '#aaa' }} />
                  <p className="text-xl font-black uppercase mb-2">No active asks</p>
                  <p className="text-sm font-medium" style={{ color: '#888' }}>The market is quiet. Check back soon.</p>
                </div>
              )}

              {!isLoading && allBids.length > 0 && (
                <>
                  <div className="space-y-3 stagger-children">
                    {allBids.slice(bidPage * BIDS_PER_PAGE, (bidPage + 1) * BIDS_PER_PAGE).map((bid) => (
                      <div
                        key={bid.bid_id}
                        className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-2xl p-4 animate-fade-in-up"
                        data-testid={`browse-bid-${bid.bid_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl border-[2px] border-[#121212] flex items-center justify-center font-black text-sm shadow-[2px_2px_0px_0px_#121212]" style={{ backgroundColor: user && bid.user_id === user.user_id ? '#E8C86A' : getAvatarColor(bid) }}>
                            {getInitial(bid, user)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-base truncate">{getDisplayName(bid, user)}</div>
                            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: '#888' }}>
                              <span>{getTimeAgo(bid.created_at)}</span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-mono text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${bid.asking_price}</div>
                            <div className="text-xs font-bold uppercase" style={{ color: '#888' }}>per tee</div>
                          </div>
                        </div>
                        {isStartup && (
                          <button
                            onClick={() => handleAcceptBid(bid.bid_id)}
                            disabled={accepting === bid.bid_id || !marketOpen}
                            className={`w-full mt-3 font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] transition-all rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 disabled:opacity-50 ${!marketOpen
                              ? 'bg-[#D8DFEA] text-[#666] cursor-not-allowed'
                              : 'bg-[#6EE7B7] text-[#121212] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#121212] active:translate-y-[3px] active:shadow-none'
                              }`}
                            data-testid={`accept-bid-${bid.bid_id}`}
                          >
                            {!marketOpen ? (
                              'Market Closed'
                            ) : accepting === bid.bid_id ? 'Accepting...' : (
                              <><Check className="w-4 h-4" strokeWidth={3} /> Accept — ${bid.asking_price}</>
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Pagination */}
                  {allBids.length > BIDS_PER_PAGE && (
                    <div className="flex items-center justify-between mt-4">
                      <button
                        onClick={() => setBidPage(p => Math.max(0, p - 1))}
                        disabled={bidPage === 0}
                        className="flex items-center gap-1 text-xs font-black uppercase border-[2px] border-[#121212] rounded-xl px-3 py-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-[#F6F1E8] shadow-[2px_2px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[3px_3px_0px_0px_#121212] active:translate-y-[2px] active:shadow-none"
                        data-testid="bids-prev"
                      >
                        <ChevronLeft className="w-4 h-4" strokeWidth={3} /> Prev
                      </button>
                      <span className="text-xs font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#888' }}>
                        {bidPage * BIDS_PER_PAGE + 1}–{Math.min((bidPage + 1) * BIDS_PER_PAGE, allBids.length)} of {allBids.length}
                      </span>
                      <button
                        onClick={() => setBidPage(p => Math.min(Math.ceil(allBids.length / BIDS_PER_PAGE) - 1, p + 1))}
                        disabled={(bidPage + 1) * BIDS_PER_PAGE >= allBids.length}
                        className="flex items-center gap-1 text-xs font-black uppercase border-[2px] border-[#121212] rounded-xl px-3 py-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-[#F6F1E8] shadow-[2px_2px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[3px_3px_0px_0px_#121212] active:translate-y-[2px] active:shadow-none"
                        data-testid="bids-next"
                      >
                        Next <ChevronRight className="w-4 h-4" strokeWidth={3} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* How it works — hidden for locked out users */}
      {!isLockedOut && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { step: '01', title: 'Name your price', desc: 'Post how much you\'d accept to wear a startup\'s tee. $200? $500? You decide.' },
              { step: '02', title: 'Startups pick you', desc: 'Startups browse all asks. They see your pseudonym, and price — then choose who they want.' },
              { step: '03', title: 'Get matched & wear', desc: 'When a startup accepts your ask, choose "buy it yourself" or "get it shipped". Wear it, earn the money.' },
            ].map((item) => (
              <div key={item.step} className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5">
                <div className="font-mono text-3xl font-bold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#FF6B6B' }}>{item.step}</div>
                <h3 className="font-black text-base uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-sm font-medium" style={{ color: '#555' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQ — hidden for locked out users */}
      {!isLockedOut && (
        <FaqSection />
      )}

      {/* Footer */}
      <footer className="border-t-[3px] border-[#121212] py-8 px-4" style={{ backgroundColor: '#EDE3C7' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-sm font-bold">© {new Date().getFullYear()} The SurfaceArea Project</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="text-xs font-bold uppercase tracking-wider hover:text-[#FF6B6B] transition-colors">Terms of Service</Link>
            <span className="text-xs" style={{ color: '#aaa' }}>·</span>
            <Link to="/privacy" className="text-xs font-bold uppercase tracking-wider hover:text-[#FF6B6B] transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FaqSection() {
  const [tab, setTab] = useState('students');

  const studentFaqs = [
    { q: 'Can I change my price?', a: 'Yes! You can update your ask anytime. Just click "Update My ask" and set a new price.' },
    { q: 'How do startups find me?', a: 'Your ask shows up in the marketplace with your name and asking price. Startups browse and accept asks that work for them.' },
    { q: 'What happens after a startup picks me?', a: 'You choose: buy the tee yourself get it printed (and get reimbursed), or have them ship it to you in Bangalore. Either way, you earn the ask amount.' },
    { q: 'Will the T-shirt cost extra?', a: 'No — the cost of the T-shirt will be deducted from your ask amount. So make sure to factor that in when you set your price!' },
  ];

  const startupFaqs = [
    { q: 'How do I find students to wear my T-shirt?', a: 'Browse the marketplace and accept asks that work for your budget. Students see your brand and you pick who represents you.' },
    { q: 'What if the student doesn\'t turn up?', a: 'If the student does not turn up, we will refund the full amount corresponding to that student to your source account.' },
    { q: 'How does payment work?', a: 'Once you accept a student\'s ask, you\'ll be directed to our secure checkout. We accept Venmo, PayPal, and Zelle for international (USD) payments, and Razorpay for payments in INR. The student is then notified and matched with you.' },
  ];

  const faqs = tab === 'students' ? studentFaqs : startupFaqs;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
      <div className="flex items-center gap-3 mb-6">
        {[
          { value: 'students', label: 'For Students' },
          { value: 'startups', label: 'For Startups' },
        ].map(t => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`text-xs font-black uppercase tracking-wider border-[2px] border-[#121212] rounded-full px-4 py-1.5 transition-all ${tab === t.value
              ? 'bg-[#121212] text-[#F6F1E8]'
              : 'bg-[#F6F1E8] shadow-[2px_2px_0px_0px_#121212] hover:-translate-y-[1px]'
              }`}
            data-testid={`faq-tab-${t.value}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {faqs.map((faq, i) => (
          <FaqItem key={`${tab}-${i}`} question={faq.q} answer={faq.a} />
        ))}
      </div>
    </div>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl overflow-hidden">
      <button className="w-full flex items-center justify-between p-5 text-left font-bold text-base" onClick={() => setOpen(!open)} data-testid={`faq-toggle-${question.slice(0, 10).replace(/\s/g, '-').toLowerCase()}`}>
        {question}
        <ChevronDown className={`w-5 h-5 transition-transform flex-shrink-0 ml-2 ${open ? 'rotate-180' : ''}`} strokeWidth={3} />
      </button>
      {open && <div className="px-5 pb-5 text-sm font-medium" style={{ color: '#555' }}>{answer}</div>}
    </div>
  );
}
